import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  AlertTriangle,
  CalendarX,
  Check,
  ChevronLeft,
  ChevronRight,
  CopyPlus,
  Info,
  Repeat,
  Redo2,
  Undo2,
  X,
} from 'lucide-react';
import {
  AdminPage,
  Button,
  ConfirmDialog,
  ErrorState,
  Skeleton,
  StatusBadge,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { cn } from '@/lib/cn';
import { formatDate, formatDateTime, toDateKey, weekStart, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { usePeriods } from './api';
import { useRecurringMutations } from './recurringApi';
import { MonthBudgetDialog } from './MonthBudgetDialog';
import { formatHours, shiftMinutes } from '../hours';
import { ShiftDrawer } from './ShiftDrawer';
import { useWeekTaskCounts } from './shiftTasksApi';
import { useSpecialDaysByDate } from './specialDaysApi';
import { DragCard, StaffPanel, WeekGrid } from './WeekGrid';
import { DayGrid } from './DayGrid';
import { MonthGrid } from './MonthGrid';
import {
  branchFill,
  useScheduleHours,
  useScheduleWeek,
  useWeekMutations,
  visibleBoard,
  type CopyResult,
  type HoursRow,
  type WeekAssignment,
  type WeekBoard,
} from './weekApi';

/**
 * לוח הסידור השבועי (PRD 5.4.4).
 *
 * שבוע אחד על פני כמה סניפים, ולא רשימה של תקופות. עם חלון
 * השבועות המתגלגל יש שישה סניפים כפול ארבעה שבועות, ורשימה של
 * 24 שורות זהות אינה מסך עבודה.
 *
 * הצבע של הסניף נוסע עם כל שיבוץ: כשרואים כמה סניפים יחד, הוא
 * מה שאומר מי איפה. הוא לעולם לא לבד, שם הסניף תמיד בתווית.
 */
/**
 * צעד אחד שאפשר לבטל.
 *
 * מתואר ולא נסגר על מזהה: שיבוץ חדש מקבל מזהה רק אחרי שהשרת
 * ענה, ו closure על ערך שעוד לא קיים מבטלת את הדבר הלא נכון.
 * כאן הצעד אומר *מה* נעשה, והביטול מוצא את השורה בלוח העדכני.
 */
type Slot = {
  branch_id: string;
  date: string;
  shift_type_id: string;
  employee_id: string;
};

/** מקום בלוח: סניף, יום ומשמרת. */
type Spot = { branch_id: string; date: string; shift_type_id: string };

/**
 * צעד בלוח, בצורה שאפשר להריץ לשני הכיוונים.
 *
 * הצעד מתאר *מה קרה*, ולא איך לבטל אותו. ההיפוך הוא פעולה
 * נפרדת ומלאה (add מול remove, ו move עם from ו to מוחלפים),
 * ולכן ביטול וביצוע מחדש הם אותו מנוע עם אותו צעד בכיוון אחר,
 * ולא שתי פיסות קוד שצריך לתחזק במקביל.
 */
type BoardStep =
  | { kind: 'add'; label: string; slots: Slot[] }
  | { kind: 'remove'; label: string; slots: Slot[] }
  | { kind: 'move'; label: string; assignmentId: string; from: Spot; to: Spot };

/** הצעד ההפוך. */
function invert(step: BoardStep): BoardStep {
  if (step.kind === 'add') return { ...step, kind: 'remove' };
  if (step.kind === 'remove') return { ...step, kind: 'add' };
  return { ...step, from: step.to, to: step.from };
}

/**
 * Ctrl+Z לביטול, Ctrl+Shift+Z או Ctrl+Y לביצוע מחדש.
 *
 * אלה הקיצורים שהיד כבר יודעת מוורד, ומי שמסדר לוח שלם לא רוצה
 * לנדוד לעכבר אחרי כל טעות. בשדה טקסט הם שייכים לשדה, ולכן שם
 * אנחנו לא נוגעים.
 *
 * קומפוננטה ולא אפקט בגוף העמוד: המנוע מוגדר אחרי ההפניה של
 * "אין הרשאה", ו hook אסור שם.
 */
function BoardShortcuts({ onTravel }: { onTravel: (direction: 'undo' | 'redo') => void }) {
  // בלי מערך תלויות: המאזין חייב לראות את המחסניות העדכניות
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;

      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        onTravel('undo');
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        onTravel('redo');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return null;
}

export function WeekBoardPage() {
  const canManage = usePerm('schedule.manage');
  const [weekStartKey, setWeekStartKey] = useState(() => toDateKey(weekStart()));
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * הסניף שההודעה שייכת לו, אם היא שייכת לאחד.
   *
   * עם חמש טבלאות אחת מתחת לשנייה, הודעה בראש העמוד נמצאת מחוץ
   * למסך כשעובדים על הסניף הרביעי: הגרירה נכשלת, שום דבר לא
   * קורה, ואין שום הסבר באזור שמסתכלים עליו.
   *
   * null = הודעה כללית, ואז היא בראש כמו קודם.
   */
  const [errorBranchId, setErrorBranchId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  /** העובד שסומן ברשימת הצוות, להארת השיבוצים שלו. */
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  /** צעדים אחרונים שאפשר לבטל. עשרים מספיקים, והם לא נשמרים. */
  const [history, setHistory] = useState<BoardStep[]>([]);
  /**
   * מה שבוטל ואפשר להחזיר.
   *
   * ביטול בלי דרך חזרה הוא מלכודת: לוחצים, מבינים שלא לזה
   * התכוונת, ועכשיו צריך לשחזר ביד מה היה שם. כמו בוורד,
   * פעולה חדשה מוחקת את מה שקדימה — אי אפשר לחזור לענף שכבר
   * נטשת.
   */
  const [future, setFuture] = useState<BoardStep[]>([]);
  /** צעד בתנועה. מונע לחיצה כפולה שתריץ את אותו צעד פעמיים. */
  const [stepping, setStepping] = useState(false);

  /**
   * מעבר שבוע מאפס את שתי המחסניות.
   *
   * צעד שמור מצביע על תאריך, ולא על "השבוע שרואים". בלי האיפוס
   * הזה לחיצה על ביטול בשבוע הבא היתה משנה שבוע אחר, מחוץ למסך,
   * בלי שאפשר לראות מה קרה. האיפוס בזמן רינדור ולא באפקט, כדי
   * שלא יהיה רגע שבו הכפתור מציע לבטל צעד של שבוע אחר.
   */
  const [stacksWeek, setStacksWeek] = useState(weekStartKey);
  if (stacksWeek !== weekStartKey) {
    setStacksWeek(weekStartKey);
    setHistory([]);
    setFuture([]);
  }

  /**
   * Escape מנקה את הסימון.
   *
   * בלי יציאה מפורשת הדרך היחידה חזרה לטבלה רגילה היתה ללחוץ
   * שוב על אותו כרטיס, וצריך לזכור על מי לחצת. מצב שקל להיכנס
   * אליו וקשה לצאת ממנו נראה כמו תקלה.
   */
  useEffect(() => {
    if (highlightedId === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHighlightedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [highlightedId]);

  // החודש של תחילת השבוע. שבוע שחוצה חודשים שייך לכאן, ולכן שם
  // החודש מוצג ליד המספר ולא משתמע.
  const month = useMemo(() => monthOf(weekStartKey), [weekStartKey]);
  const weekEndKey = useMemo(() => {
    const end = new Date(`${weekStartKey}T12:00:00`);
    end.setDate(end.getDate() + 6);
    return toDateKey(end);
  }, [weekStartKey]);
  const monthHours = useScheduleHours(month.from, month.to);

  const board = useScheduleWeek(weekStartKey);

  /**
   * מועדי פרסום מתוזמנים, לפי מזהה תקופה.
   *
   * מגיע מרשימת התקופות ולא מלוח השבוע: זו שורה אחת בשאילתה
   * שכבר קיימת במערכת, ולא שדה נוסף ב RPC הגדול של הלוח.
   */
  const periods = usePeriods();
  const scheduled = useMemo(
    () =>
      new Map(
        (periods.data ?? [])
          .filter((period) => period.publish_at)
          .map((period) => [period.id, period.publish_at as string]),
      ),
    [periods.data],
  );

  /** שם לכל מזהה סניף, לתווית "מאיזה סניף" אצל אורח. */
  const branchNames = useMemo(
    () => new Map((board.data?.branches ?? []).map((branch) => [branch.id, branch.name])),
    [board.data],
  );
  const taskCounts = useWeekTaskCounts(weekStartKey, weekEndKey);
  const specialDays = useSpecialDaysByDate(weekStartKey, weekEndKey);
  const { applyWeek, previewWeek } = useRecurringMutations();
  const { assign, move, unassign, copyWeek, updateAssignment, closeShift, reopenShift } =
    useWeekMutations();
  const confirm = useConfirm();
  const [notice, setNotice] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /** העובד שפתחו לו את חלון המכסה החודשית. */
  const [budgetFor, setBudgetFor] = useState<{
    id: string;
    name: string;
    weekly: number | null;
    monthly: number | null;
  } | null>(null);
  const [closing, setClosing] = useState<{
    branchId: string;
    date: string;
    shiftTypeId: string;
  } | null>(null);

  // מרחק הפעלה כדי שלחיצה על כפתור ההסרה לא תיחשב תחילת גרירה.
  // חיישן מקלדת כי לוח שעובד בגרירה בלבד אינו נגיש.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  /**
   * שבוע או יום.
   *
   * לא שני מסכים אלא שני צירים על אותם נתונים: השבועי עונה על
   * "איך נראה השבוע בסניף", והיומי על "מי עובד מחר בכל הרשת".
   * הלוח כבר טעון, ולכן המעבר אינו עולה קריאה לשרת.
   */
  const [dayKey, setDayKey] = useState<string | null>(null);

  /**
   * תצוגת חודש, לסניף אחד בלבד.
   *
   * לוח חודשי של חמישה סניפים הוא חמש שכבות על אותו תא. לכן
   * המתג מופיע רק כשנבחר סניף יחיד, ונעלם מעצמו כשחוזרים
   * ל"כל הסניפים" — בלי להשאיר את המסך במצב שאי אפשר לצאת ממנו.
   */
  const [isMonth, setIsMonth] = useState(false);
  const onlyBranchId = branchIds.length === 1 ? branchIds[0] : null;
  const monthView = isMonth && onlyBranchId !== null;

  function shiftMonth(byMonths: number) {
    const base = new Date(`${weekStartKey}T12:00:00`);
    base.setDate(1);
    base.setMonth(base.getMonth() + byMonths);
    setWeekStartKey(toDateKey(weekStart(base)));
  }

  function shiftDay(byDays: number) {
    const base = new Date(`${dayKey ?? weekStartKey}T12:00:00`);
    base.setDate(base.getDate() + byDays);
    const next = toDateKey(base);
    // מחוץ לשבוע המוצג: הלוח עובר איתו
    setDayKey(next);
    setWeekStartKey(toDateKey(weekStart(base)));
  }

  function shiftWeek(byDays: number) {
    const next = new Date(`${weekStartKey}T12:00:00`);
    next.setDate(next.getDate() + byDays);
    setWeekStartKey(toDateKey(weekStart(next)));
  }

  function onDragEnd(event: DragEndEvent) {
    setDragging(null);
    setErrorBranchId(null);
    const { active, over } = event;
    if (!over) return;

    // המזהה של התא: branchId|date|shiftTypeId
    const [branchId, date, shiftTypeId] = String(over.id).split('|');
    if (!branchId || !date || !shiftTypeId) return;

    const payload = active.data.current as
      | { kind: 'staff'; employeeId: string }
      | { kind: 'assignment'; assignment: WeekAssignment }
      | undefined;
    if (!payload) return;

    setError(null);
    setErrorBranchId(null);
    const run = (promise: Promise<unknown>) =>
      void promise.catch((caught: unknown) => {
        setError(errorMessage(caught));
        setErrorBranchId(branchId);
      });

    /**
     * מכסה היא מידע ולא חסימה: המציאות גוברת עליה, ומי שמישהי
     * שלו התקשרה חולה בשבת בבוקר חייב לשבץ גם את מי שכבר עבר.
     * מערכת שחוסמת שם מלמדת למחוק את המכסה, ואז המספר האדום
     * כבר לא אומר כלום. לכן שואלים, ולא מסרבים.
     */
    function withBudgetCheck(employeeId: string, added: number, act: () => void) {
      const warning = data
        ? budgetWarning(data, monthHours.data, employeeId, added, month.label)
        : null;
      if (warning === null) {
        act();
        return;
      }
      confirm.ask({
        title: 'חריגה ממכסת השעות',
        body: warning,
        confirmLabel: 'לשבץ בכל זאת',
        tone: 'warn',
        onConfirm: act,
      });
    }

    if (payload.kind === 'staff') {
      const added = shiftLength(data, shiftTypeId);
      const person = data?.staff.find((one) => one.id === payload.employeeId);

      const place = () =>
        withBudgetCheck(payload.employeeId, added, () =>
          run(
            assign
              .mutateAsync({
                branch_id: branchId,
                date,
                shift_type_id: shiftTypeId,
                employee_id: payload.employeeId,
              })
              .then(() =>
                remember({
                  kind: 'add',
                  label: `שיבוץ ${person?.name ?? 'עובד/ת'}`,
                  slots: [
                    {
                      branch_id: branchId,
                      date,
                      shift_type_id: shiftTypeId,
                      employee_id: payload.employeeId,
                    },
                  ],
                }),
              ),
          ),
        );

      /**
       * עובד של סניף אחר: שואלים, לא חוסמים.
       *
       * זה קורה באמת — מחליפים מישהו שחלה — אבל הוא גם מה
       * שקורה כשגוררים בטעות אל הטבלה הלא נכונה, ושם זה נגמר
       * בסידור שאיש לא בודק. השאלה עולה רק כשזה חריג.
       */
      if (person && !person.branch_ids.includes(branchId)) {
        const target = data?.branches.find((one) => one.id === branchId);
        confirm.ask({
          title: 'עובד/ת מסניף אחר',
          body: `${person.name} אינו/ה משויך/ת ל${target?.name ?? 'סניף הזה'}. לשבץ בכל זאת?`,
          confirmLabel: 'לשבץ בכל זאת',
          tone: 'warn',
          onConfirm: place,
        });
        return;
      }

      place();
      return;
    }

    const a = payload.assignment;
    if (a.branch_id === branchId && a.date === date && a.shift_type_id === shiftTypeId) return;

    // שיבוץ עם שעות מיוחדות שומר אותן גם אחרי ההזזה, ולכן המשך
    // אינו משתנה. בלי שעות מיוחדות הוא לובש את שעות המשמרת החדשה.
    const added =
      a.start_time !== null
        ? 0
        : shiftLength(data, shiftTypeId) - shiftLength(data, a.shift_type_id);

    const moved = data?.staff.find((one) => one.id === a.employee_id);

    withBudgetCheck(a.employee_id, added, () =>
      run(
        move
          .mutateAsync({
            assignment_id: a.id,
            branch_id: branchId,
            date,
            shift_type_id: shiftTypeId,
          })
          .then(() =>
            remember({
              kind: 'move',
              label: `הזזת ${moved?.name ?? 'משמרת'}`,
              assignmentId: a.id,
              // המזהה שורד הזזה, ולכן אפשר להחזיר אותו למקומו
              // ולהזיז אותו שוב קדימה
              from: {
                branch_id: a.branch_id,
                date: a.date,
                shift_type_id: a.shift_type_id,
              },
              to: { branch_id: branchId, date, shift_type_id: shiftTypeId },
            }),
          ),
      ),
    );
  }

  /**
   * העתקת השבוע הקודם. לא דורסת, ומדלגת על מה שכבר אינו תקף.
   * הדיווח נאמר במלואו: מנהל שלא יידע שדילגנו על שלושה אנשים
   * יפרסם סידור חסר.
   *
   * קודם ריצה יבשה ואז שאלה. הפעולה כותבת עשרות שיבוצים בכמה
   * סניפים בלחיצה אחת, ועד עכשיו "מה זה הולך לעשות" נענה רק
   * אחרי שזה כבר קרה. המספרים בשאלה הם של השרת, מאותה שאילתה
   * שתעשה את העבודה — לא הערכה.
   */
  function askCopyWeek() {
    setError(null);
    setErrorBranchId(null);
    setNotice(null);

    void copyWeek
      .mutateAsync({ week_start: weekStartKey, branch_ids: branchIds, dryRun: true })
      .then((preview) => {
        if (preview.copied === 0) {
          setNotice(copyMessage(preview));
          return;
        }

        confirm.ask({
          title: 'העתקת השבוע הקודם',
          body: <BulkPreview rows={preview.branches} total={preview.copied} skipped={copySkips(preview)} />,
          confirmLabel:
            preview.copied === 1 ? 'להעתיק משמרת אחת' : `להעתיק ${preview.copied} משמרות`,
          onConfirm: () => {
            setError(null);
            setNotice(null);
            void copyWeek
              .mutateAsync({ week_start: weekStartKey, branch_ids: branchIds })
              .then((result) => setNotice(copyMessage(result)))
              .catch((caught: unknown) => setError(errorMessage(caught)));
          },
        });
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  /**
   * מילוי משמרות קבועות, עם אותה תצוגה מקדימה.
   *
   * קודם לא נשאלה כאן שום שאלה בכלל: לחיצה אחת יצרה עשרות
   * שיבוצים בחמישה סניפים, ומי שלחץ על השבוע הלא נכון גילה
   * את זה מהתוצאה.
   */
  function askApplyRecurring() {
    setError(null);
    setErrorBranchId(null);
    setNotice(null);

    void previewWeek
      .mutateAsync({ week_start: weekStartKey, branch_ids: branchIds })
      .then((preview) => {
        if (preview.total === 0) {
          setNotice('אין משמרות קבועות להוסיף לשבוע הזה.');
          return;
        }

        confirm.ask({
          title: 'מילוי משמרות קבועות',
          body: <BulkPreview rows={preview.branches} total={preview.total} skipped={[]} />,
          confirmLabel:
            preview.total === 1 ? 'להוסיף משמרת אחת' : `להוסיף ${preview.total} משמרות`,
          onConfirm: () => {
            setError(null);
            setNotice(null);
            void applyWeek
              .mutateAsync({ week_start: weekStartKey, branch_ids: branchIds })
              .then((added) =>
                setNotice(
                  added === 1 ? 'נוספה משמרת קבועה אחת.' : `נוספו ${added} משמרות קבועות.`,
                ),
              )
              .catch((caught: unknown) => setError(errorMessage(caught)));
          },
        });
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  if (!canManage) return <Navigate to="/admin" replace />;

  const data = board.data;
  // רשימת הבחירה תמיד מכל הסניפים, והלוח רק מהנבחרים
  const shown = data ? visibleBoard(data, branchIds) : null;

  /**
   * פינוי עובד מכל השבוע בלחיצה אחת.
   *
   * אחת ולא כמה: מי שמבטל מישהו מבטל אותו מכל השבוע, ועשר
   * לחיצות על איקסים קטנים הן עשר הזדמנויות לפספס אחת.
   *
   * שואלים לפני, כי זו פעולה שמוחקת הרבה בבת אחת, ומשמרת
   * בסידור שפורסם אינה נוגעת — היא נעולה כמו בכל מקום אחר.
   */
  function clearWeek(person: { id: string; name: string }, shifts: number) {
    if (shifts === 0) return;

    const locked = new Set(
      (data?.branches ?? [])
        .filter((branch) => branch.status === 'published')
        .map((branch) => branch.id),
    );
    const mine = (data?.assignments ?? []).filter(
      (one) => one.employee_id === person.id && !locked.has(one.branch_id),
    );

    if (mine.length === 0) {
      setError('כל המשמרות שלו/ה בשבוע הזה בסידור שפורסם, ולכן הן נעולות.');
      return;
    }

    const slots = mine.map((one) => ({
      branch_id: one.branch_id,
      date: one.date,
      shift_type_id: one.shift_type_id,
      employee_id: one.employee_id,
    }));

    confirm.ask({
      title: 'פינוי מכל השבוע',
      body:
        mine.length === shifts
          ? `כל ${shifts} המשמרות של ${person.name} בשבוע הזה יוסרו.`
          : `${mine.length} מתוך ${shifts} המשמרות של ${person.name} יוסרו. השאר בסידור שכבר פורסם, ולכן נעול.`,
      confirmLabel: 'להסיר הכל',
      tone: 'warn',
      onConfirm: () => {
        setError(null);
        setErrorBranchId(null);
        setNotice(null);
        void Promise.all(mine.map((one) => unassign.mutateAsync(one.id)))
          .then(() => {
            remember({
              kind: 'remove',
              label: `פינוי ${person.name} מהשבוע`,
              slots,
            });
            setNotice(`${person.name} הוסר/ה מ${mine.length} משמרות.`);
          })
          .catch((caught: unknown) => setError(errorMessage(caught)));
      },
    });
  }

  /**
   * פינוי כל השיבוצים של סניף אחד בשבוע הזה.
   *
   * קיים כי השגרה היא "התחלתי לא נכון, בוא נתחיל מחדש", והחלופה
   * היא ארבעה עשר איקסים. סידור שפורסם נעול, כמו בכל מקום אחר,
   * ולכן הכפתור לא מופיע עליו בכלל.
   */
  function clearBranchWeek(branch: { id: string; name: string }) {
    const mine = (data?.assignments ?? []).filter((one) => one.branch_id === branch.id);
    if (mine.length === 0) return;

    const slots = mine.map((one) => ({
      branch_id: one.branch_id,
      date: one.date,
      shift_type_id: one.shift_type_id,
      employee_id: one.employee_id,
    }));

    confirm.ask({
      title: `פינוי השבוע ב${branch.name}`,
      body: `כל ${mine.length} השיבוצים של ${branch.name} בשבוע הזה יוסרו. אפשר לבטל מיד אחרי.`,
      confirmLabel: 'להסיר הכל',
      tone: 'warn',
      onConfirm: () => {
        setError(null);
        setErrorBranchId(null);
        setNotice(null);
        void Promise.all(mine.map((one) => unassign.mutateAsync(one.id)))
          .then(() => {
            remember({ kind: 'remove', label: `פינוי השבוע ב${branch.name}`, slots });
            setNotice(`${mine.length} שיבוצים הוסרו מ${branch.name}.`);
          })
          .catch((caught: unknown) => setError(errorMessage(caught)));
      },
    });
  }

  // נקרא מחדש מהלוח ולא נשמר ב state, כדי שהמגש יראה את מה
  // שבשרת אחרי שמירה ולא עותק שהתיישן
  const openAssignment = data?.assignments.find((one) => one.id === openId) ?? null;
  const openBranch = openAssignment
    ? data?.branches.find((one) => one.id === openAssignment.branch_id)
    : null;

  /** פעולה חדשה מאפסת את הדרך קדימה. */
  const remember = (step: BoardStep) => {
    setHistory((current) => [...current, step].slice(-20));
    setFuture([]);
  };

  /** השורה שיושבת כרגע במקום הזה, אם היא קיימת. */
  const rowAt = (slot: Slot) =>
    (data?.assignments ?? []).find(
      (one) =>
        one.branch_id === slot.branch_id &&
        one.date === slot.date &&
        one.shift_type_id === slot.shift_type_id &&
        one.employee_id === slot.employee_id,
    );

  /**
   * הרצת צעד מול הלוח *העדכני*, ולא מול צילום ישן: בין הפעולה
   * לביטול שלה מישהו אחר אולי שינה משהו, ומזהה שנשמר בצד עלול
   * כבר לא להתקיים.
   */
  function perform(step: BoardStep): Promise<unknown> {
    if (step.kind === 'add') return Promise.all(step.slots.map((slot) => assign.mutateAsync(slot)));

    if (step.kind === 'remove') {
      const rows = step.slots.flatMap((slot) => {
        const row = rowAt(slot);
        return row ? [row] : [];
      });
      if (rows.length === 0) return Promise.reject(new Error('השיבוץ כבר לא קיים בלוח.'));
      return Promise.all(rows.map((row) => unassign.mutateAsync(row.id)));
    }

    return move.mutateAsync({ assignment_id: step.assignmentId, ...step.to });
  }

  /**
   * צעד אחורה או קדימה.
   *
   * הצעד עובר בין המחסניות רק *אחרי* שהשרת אישר. נכשל באמצע —
   * הוא נשאר איפה שהיה, וכפתור הביטול עדיין מציע אותו, במקום
   * להיעלם ולהשאיר את הלוח במצב שאי אפשר לתקן.
   */
  function travel(direction: 'undo' | 'redo') {
    const step = (direction === 'undo' ? history : future).at(-1);
    if (!step || stepping) return;

    setError(null);
    setErrorBranchId(null);
    setNotice(null);
    setStepping(true);

    void perform(direction === 'undo' ? invert(step) : step)
      .then(() => {
        if (direction === 'undo') {
          setHistory((current) => current.slice(0, -1));
          setFuture((current) => [...current, step]);
        } else {
          setFuture((current) => current.slice(0, -1));
          setHistory((current) => [...current, step]);
        }
        setNotice(direction === 'undo' ? `בוטל: ${step.label}` : `בוצע מחדש: ${step.label}`);
      })
      .catch((caught: unknown) => setError(errorMessage(caught)))
      .finally(() => setStepping(false));
  }

  function removeAssignment(id: string) {
    setError(null);
    setErrorBranchId(null);
    setOpenId(null);
    const row = (data?.assignments ?? []).find((one) => one.id === id);
    const person = row ? data?.staff.find((one) => one.id === row.employee_id) : undefined;

    void unassign
      .mutateAsync(id)
      .then(() => {
        if (!row) return;
        remember({
          kind: 'remove',
          label: `הסרת ${person?.name ?? 'עובד/ת'} ממשמרת`,
          slots: [
            {
              branch_id: row.branch_id,
              date: row.date,
              shift_type_id: row.shift_type_id,
              employee_id: row.employee_id,
            },
          ],
        });
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  return (
    <AdminPage
      title="סידור עבודה"
      subtitle={
        // טווח התאריכים בעטיפת ltr: בפסקה בעברית המקף שבין שני
        // מספרים נחשב ניטרלי, ושני התאריכים מתהפכים.
        data ? (
          <span dir="ltr">
            {formatDate(data.week_start)} – {formatDate(data.week_end)}
          </span>
        ) : (
          'טוען'
        )
      }
      width="full"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <BoardShortcuts onTravel={travel} />

          {/*
            ביטול הצעד האחרון. מופיע רק כשיש מה לבטל, ואומר *מה*
            יתבטל: "בטל" לבדו מחייב לזכור מה עשית לפני דקה.
          */}
          {history.length > 0 && (
            <Button variant="secondary" disabled={stepping} onClick={() => travel('undo')}>
              <Undo2
                size={18}
                strokeWidth={1.75}
                aria-hidden="true"
                className="rtl:-scale-x-100"
              />
              ביטול: {history[history.length - 1].label}
            </Button>
          )}

          {/*
            ביצוע מחדש. מופיע רק אחרי ביטול, ונעלם ברגע שנעשתה
            פעולה חדשה — בדיוק כמו בוורד, וכדי שלא יישאר על המסך
            כפתור שמציע להחזיר משהו שכבר אינו רלוונטי.
          */}
          {future.length > 0 && (
            <Button variant="secondary" disabled={stepping} onClick={() => travel('redo')}>
              <Redo2
                size={18}
                strokeWidth={1.75}
                aria-hidden="true"
                className="rtl:-scale-x-100"
              />
              ביצוע מחדש: {future[future.length - 1].label}
            </Button>
          )}

          <Button variant="secondary" disabled={copyWeek.isPending} onClick={askCopyWeek}>
            <CopyPlus size={18} strokeWidth={1.75} aria-hidden="true" />
            {copyWeek.isPending ? 'מעתיק' : 'העתקת השבוע הקודם'}
          </Button>

          {/*
            שבוע שנפתח אחרי שנקבעה תבנית כבר מגיע מלא. הכפתור הזה
            נחוץ לשבועות שנפתחו לפני כן, ואחרי שינוי בתבניות.
          */}
          <Button
            variant="secondary"
            disabled={applyWeek.isPending || previewWeek.isPending}
            onClick={askApplyRecurring}
          >
            <Repeat size={18} strokeWidth={1.75} aria-hidden="true" />
            {applyWeek.isPending ? 'ממלא' : previewWeek.isPending ? 'בודק' : 'מילוי משמרות קבועות'}
          </Button>

          {/*
            ימים סגורים יושבים כאן ולא בתפריט הצדדי. זה חלק
            מהסידור ולא מסך נפרד, ומי שנכנס אליו מגיע מתוך לוח
            שבו ראה יום ריק.
          */}
          <Link to="/admin/schedule/closures" className="no-underline">
            <Button variant="secondary">
              <CalendarX size={18} strokeWidth={1.75} aria-hidden="true" />
              ימים סגורים
            </Button>
          </Link>

          {/* מתג התצוגה. לפני חיצי הניווט, כי הוא קובע על מה הם
              פועלים — שבוע או יום. */}
          <div className="inline-flex rounded-sm border border-muted">
            <button
              type="button"
              aria-pressed={dayKey === null && !monthView}
              onClick={() => {
                setDayKey(null);
                setIsMonth(false);
              }}
              className={cn(
                'min-h-11 cursor-pointer border-e border-muted px-3 text-body-sm font-medium',
                dayKey === null && !monthView
                  ? 'bg-charcoal text-on-charcoal'
                  : 'bg-transparent text-ink hover:bg-cream',
              )}
            >
              שבוע
            </button>
            <button
              type="button"
              aria-pressed={dayKey !== null}
              onClick={() => {
                setIsMonth(false);
                setDayKey(dayKey ?? toDateKey(new Date()));
              }}
              className={cn(
                'min-h-11 cursor-pointer px-3 text-body-sm font-medium',
                dayKey !== null && !monthView
                  ? 'bg-charcoal text-on-charcoal'
                  : 'bg-transparent text-ink hover:bg-cream',
              )}
            >
              יום
            </button>

            {/* רק לסניף אחד. בחמישה סניפים זה לוח שאי אפשר לקרוא. */}
            {onlyBranchId !== null && (
              <button
                type="button"
                aria-pressed={monthView}
                onClick={() => {
                  setDayKey(null);
                  setIsMonth(true);
                }}
                className={cn(
                  'min-h-11 cursor-pointer border-s border-muted px-3 text-body-sm font-medium',
                  monthView ? 'bg-charcoal text-on-charcoal' : 'bg-transparent text-ink hover:bg-cream',
                )}
              >
                חודש
              </button>
            )}
          </div>

          <div className="inline-flex rounded-sm border border-muted">
            <button
              type="button"
              aria-label={monthView ? 'החודש הקודם' : dayKey ? 'היום הקודם' : 'השבוע הקודם'}
              onClick={() => (monthView ? shiftMonth(-1) : dayKey ? shiftDay(-1) : shiftWeek(-7))}
              className="inline-flex min-h-11 w-11 cursor-pointer items-center justify-center bg-transparent text-ink hover:bg-cream"
            >
              <ChevronRight size={20} strokeWidth={1.75} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => {
                setWeekStartKey(toDateKey(weekStart()));
                if (dayKey) setDayKey(toDateKey(new Date()));
              }}
              className="min-h-11 cursor-pointer border-x border-muted bg-transparent px-3 text-body-sm font-medium text-ink hover:bg-cream"
            >
              {monthView ? 'החודש' : dayKey ? 'היום' : 'השבוע'}
            </button>
            <button
              type="button"
              aria-label={monthView ? 'החודש הבא' : dayKey ? 'היום הבא' : 'השבוע הבא'}
              onClick={() => (monthView ? shiftMonth(1) : dayKey ? shiftDay(1) : shiftWeek(7))}
              className="inline-flex min-h-11 w-11 cursor-pointer items-center justify-center bg-transparent text-ink hover:bg-cream"
            >
              <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </div>
      }
    >
      {confirm.dialog}

      {/* בתצוגת יום אין טבלה לכל סניף, ולכן שגיאה שסומנה
          לסניף מסוים לא הייתה מוצגת בשום מקום */}
      {error && (errorBranchId === null || dayKey !== null) && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {notice && (
        <p
          role="status"
          className="m-0 flex items-start gap-2 rounded-sm border border-line bg-cream p-2 text-body-sm text-ink"
        >
          <Info size={16} strokeWidth={1.75} aria-hidden="true" className="mt-0.5 shrink-0" />
          {notice}
        </p>
      )}

      {board.isPending && <Skeleton className="h-96" />}
      {board.isError && <ErrorState onRetry={() => void board.refetch()} />}

      {data && shown && (
        <>
          <BranchPicker branches={data.branches} selected={branchIds} onChange={setBranchIds} />

          {/*
            מצב הסימון נאמר במפורש, עם דרך אחת ברורה לצאת ממנו.
            כשכל הלוח מועם חוץ מכמה ריבועים, מי שלא זוכר שלחץ
            חושב שהלוח נשבר.
          */}
          {highlightedId !== null && (
            <div className="flex flex-wrap items-center gap-2 rounded-sm border border-ink bg-olive-soft p-2">
              <span className="text-body-sm text-ink">
                מסומן:{' '}
                <strong className="font-semibold">
                  {data.staff.find((one) => one.id === highlightedId)?.name ?? 'עובד/ת'}
                </strong>
                . שאר המשמרות מועמות.
              </span>
              <span className="grow" />
              <Button variant="secondary" onClick={() => setHighlightedId(null)}>
                <X size={16} strokeWidth={2} aria-hidden="true" />
                ניקוי הסימון
              </Button>
            </div>
          )}

          {/*
            תצוגת חודש. מחוץ ל DndContext בכוונה: אין בה גרירה,
            ותא של יום בלוח חודשי קטן מכדי להבטיח אותה. לחיצה
            על יום פותחת את השבוע שלו, ושם משבצים.
          */}
          {monthView && onlyBranchId !== null && (
            <MonthGrid
              month={weekStartKey}
              branchId={onlyBranchId}
              onPickDay={(date) => {
                setIsMonth(false);
                setWeekStartKey(toDateKey(weekStart(new Date(`${date}T12:00:00`))));
              }}
            />
          )}

          {!monthView && (
          <DndContext
            sensors={sensors}
            accessibility={{ announcements }}
            onDragStart={(event: DragStartEvent) => setDragging(String(event.active.id))}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDragging(null)}
          >
            {/*
              תצוגת יום: צוות אחד למעלה, ואחריו כל הסניפים.

              הצוות אינו מפוצל לסניפים כאן, כי התא הוא זה שיודע
              לאיזה סניף הוא שייך. גרירה של אותו אדם לשורה אחרת
              משבצת אותו בסניף אחר, וזה בדיוק מה שהתצוגה הזו
              אמורה לאפשר.
            */}
            {dayKey !== null && (
              <div className="grid gap-3">
                <StaffPanel
                  board={shown}
                  panelId="day"
                  otherStaff={[]}
                  branchNames={branchNames}
                  highlightedId={highlightedId}
                  onHighlight={setHighlightedId}
                  onClearWeek={clearWeek}
                  allAssignments={data.assignments}
                  monthly={monthHours.data}
                  monthLabel={month.label}
                  onEditBudget={(person, row) =>
                    setBudgetFor({
                      id: person.id,
                      name: person.name,
                      weekly: row?.weekly_target ?? null,
                      monthly: row?.monthly_target ?? null,
                    })
                  }
                />

                <DayGrid
                  board={shown}
                  date={dayKey}
                  isDragging={dragging !== null}
                  onRemove={removeAssignment}
                  onOpen={(assignment) => setOpenId(assignment.id)}
                  taskCounts={taskCounts.data}
                  specialDays={specialDays.data}
                  highlightedId={highlightedId}
                  people={data.staff}
                  onClose={(cell) => setClosing(cell)}
                  onReopen={(cell) => {
                    setError(null);
                    setErrorBranchId(null);
                    setNotice(null);
                    void reopenShift
                      .mutateAsync(cell)
                      .catch((caught: unknown) => setError(errorMessage(caught)));
                  }}
                />
              </div>
            )}

            {/*
              טבלה לכל סניף, והצוות שלו ברצועה מעליה.

              קודם כל הסניפים ישבו בתוך אותו תא, והעין הייתה צריכה
              לפרק אותם לפי צבע. מי שמשבץ עובד בסניף אחד ולא
              ב"כל הרשת ביום שלישי", ולכן הסניף הוא היחידה.

              הצוות היה עמודה לצד הלוח, וזו הייתה טעות: גובה הסניף
              נקבע לפי הרשימה ולא לפי הלוח, סניף עם שתי משמרות
              ביום תפס מסך שלם, וביניהם נפערו רווחים ריקים. רצועה
              מאוזנת מתחת לכותרת פותרת את שניהם ונותנת ללוח את כל
              הרוחב.
            */}
            {dayKey === null && (
              <div className="grid gap-6">
              {shown.branches.map((branch) => {
                const only = visibleBoard(shown, [branch.id]);
                const mine = new Set(only.staff.map((person) => person.id));

                return (
                  <section key={branch.id} className="grid gap-2">
                    {/*
                      מצב הסניף יושב בכותרת שלו ולא ברצועה נפרדת
                      למעלה. הרצועה חזרה על אותם סניפים שכבר
                      מופיעים בבורר, ודחפה את הלוח הראשון מתחת
                      לקיפול — ומצב של סניף אחד נחוץ בדיוק כשמסתכלים
                      על הלוח שלו.
                    */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h2 className="m-0 flex items-center gap-2 text-title font-semibold text-ink">
                        <span
                          aria-hidden="true"
                          className="size-3 shrink-0 rounded-pill border border-line"
                          style={branch.color ? { backgroundColor: branch.color } : undefined}
                        />
                        {branch.name}
                      </h2>
                      <BranchStatus branch={branch} scheduled={scheduled} />

                      <BranchFill {...branchFill(only, branch.id)} />

                      <span className="grow" />

                      {branch.status !== 'published' &&
                        only.assignments.length > 0 && (
                          <Button
                            variant="ghost"
                            onClick={() => clearBranchWeek({ id: branch.id, name: branch.name })}
                          >
                            <X size={16} strokeWidth={2} aria-hidden="true" />
                            פינוי השבוע
                          </Button>
                        )}
                    </div>

                    <div className="grid gap-3">
                      <StaffPanel
                        board={only}
                        panelId={branch.id}
                        branchName={branch.name}
                        otherStaff={(data?.staff ?? []).filter((person) => !mine.has(person.id))}
                        branchNames={branchNames}
                        highlightedId={highlightedId}
                        onHighlight={setHighlightedId}
                        onClearWeek={clearWeek}
                        allAssignments={data.assignments}
                        monthly={monthHours.data}
                        monthLabel={month.label}
                        onEditBudget={(person, row) =>
                          setBudgetFor({
                            id: person.id,
                            name: person.name,
                            weekly: row?.weekly_target ?? null,
                            monthly: row?.monthly_target ?? null,
                          })
                        }
                      />

                      <div className="grid content-start gap-2">
                        <WeekGrid
                          board={only}
                          isDragging={dragging !== null}
                          onRemove={removeAssignment}
                          onOpen={(assignment) => setOpenId(assignment.id)}
                          taskCounts={taskCounts.data}
                          specialDays={specialDays.data}
                          highlightedId={highlightedId}
                          people={data.staff}
                          onClose={(cell) => setClosing(cell)}
                          onReopen={(cell) => {
                            setError(null);
                            setErrorBranchId(null);
                            setNotice(null);
                            void reopenShift
                              .mutateAsync(cell)
                              .catch((caught: unknown) => setError(errorMessage(caught)));
                          }}
                        />

                        {error && errorBranchId === branch.id && (
                          <p
                            role="alert"
                            className="m-0 flex items-start gap-2 rounded-sm border border-bad bg-raised p-2 text-body-sm text-bad"
                          >
                            <AlertTriangle
                              size={16}
                              strokeWidth={2}
                              aria-hidden="true"
                              className="mt-0.5 shrink-0"
                            />
                            <span>
                              {error}
                              {/*
                                ההודעה על סידור נעול אומרת "צריך
                                לפתוח אותו מחדש". בלי הקישור הזה
                                היא הוראה בלי דרך לבצע אותה.
                              */}
                              {branch.status === 'published' && branch.period_id && (
                                <>
                                  {' '}
                                  <Link
                                    to={`/admin/schedule/${branch.period_id}`}
                                    className="text-ink underline"
                                  >
                                    פתיחה מחדש של {branch.name}
                                  </Link>
                                </>
                              )}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                );
              })}
              </div>
            )}

            {/* מה שמחזיקים עם העכבר */}
            <DragOverlay dropAnimation={null}>
              {data && <DragCard board={data} activeId={dragging} />}
            </DragOverlay>
          </DndContext>
          )}

          {closing && (
            <CloseShiftDialog
              board={data}
              cell={closing}
              isPending={closeShift.isPending}
              onCancel={() => setClosing(null)}
              onConfirm={(input) => {
                setError(null);
                setErrorBranchId(null);
                setNotice(null);
                void closeShift
                  .mutateAsync(input)
                  .then((removed) => {
                    setClosing(null);
                    if (removed > 0) {
                      setNotice(
                        removed === 1
                          ? 'היום נסגר, ושיבוץ אחד שהיה בו הוסר.'
                          : `היום נסגר, ו־${removed} שיבוצים שהיו בו הוסרו.`,
                      );
                    }
                  })
                  .catch((caught: unknown) => setError(errorMessage(caught)));
              }}
            />
          )}

          {budgetFor && (
            <MonthBudgetDialog
              employeeId={budgetFor.id}
              employeeName={budgetFor.name}
              month={month.from}
              monthLabel={month.label}
              defaults={{ weekly: budgetFor.weekly, monthly: budgetFor.monthly }}
              onClose={() => setBudgetFor(null)}
            />
          )}

          {openAssignment && (
            <ShiftDrawer
              board={data}
              assignment={openAssignment}
              isPublished={openBranch?.status === 'published'}
              isPending={updateAssignment.isPending}
              onClose={() => setOpenId(null)}
              onRemove={() => removeAssignment(openAssignment.id)}
              onSave={(input) =>
                updateAssignment.mutateAsync({
                  assignment_id: openAssignment.id,
                  ...input,
                })
              }
            />
          )}
        </>
      )}
    </AdminPage>
  );
}

/**
 * מה קרה בהעתקה, במשפט אחד.
 *
 * הדילוגים נאמרים ולא נבלעים: מנהל שחושב שהשבוע הועתק במלואו
 * יפרסם סידור חסר בלי לדעת.
 */
/**
 * מה בדיוק יקרה, לפני שזה קורה.
 *
 * פירוט לפי סניף ולא רק סך הכול: הפעולה חלה על כל הסניפים
 * המוצגים בבת אחת, ו"ייווצרו 23 משמרות" אינו אומר אם משהו עומד
 * לקרות בסניף שלא התכוונת אליו.
 */
function BulkPreview({
  rows,
  total,
  skipped,
}: {
  rows: { branch_id: string; name: string; copied: number }[];
  total: number;
  skipped: string[];
}) {
  return (
    <div className="grid gap-2">
      <p className="m-0">
        {total === 1 ? 'תיווצר משמרת אחת' : `ייווצרו ${total} משמרות`}
        {rows.length === 1 ? ` ב${rows[0].name}.` : ` ב־${rows.length} סניפים.`} שיבוץ קיים לא
        יידרס.
      </p>

      {rows.length > 1 && (
        <ul className="m-0 grid list-none gap-1 p-0">
          {rows.map((row) => (
            <li key={row.branch_id} className="flex items-center justify-between gap-2 text-body-sm">
              <span>{row.name}</span>
              <span className="tabular text-muted">{row.copied}</span>
            </li>
          ))}
        </ul>
      )}

      {skipped.length > 0 && (
        <p className="m-0 text-body-sm text-muted">{skipped.join('. ')}.</p>
      )}
    </div>
  );
}

/** מה ידולג, כמשפטים קצרים. אותו ניסוח כמו בדיווח שאחרי. */
function copySkips(result: CopyResult): string[] {
  return [
    result.busy > 0 && `${result.busy} ידולגו כי העובד/ת כבר משובץ/ת באותו יום`,
    result.closed > 0 && `${result.closed} ידולגו כי היום סגור`,
    result.gone > 0 && `${result.gone} ידולגו כי העובד/ת כבר אינו/ה בסניף`,
  ].filter((part): part is string => part !== false);
}

function copyMessage(result: CopyResult): string {
  const copied =
    result.copied === 0
      ? 'לא הועתקה אף משמרת'
      : result.copied === 1
        ? 'הועתקה משמרת אחת'
        : `הועתקו ${result.copied} משמרות`;

  const skipped = [
    result.busy > 0 && `${result.busy} דולגו כי העובד/ת כבר משובץ/ת באותו יום`,
    result.closed > 0 && `${result.closed} דולגו כי היום סגור`,
    result.gone > 0 && `${result.gone} דולגו כי העובד/ת כבר אינו/ה בסניף`,
  ].filter((part): part is string => part !== false);

  if (skipped.length === 0) {
    return `${copied} מהשבוע הקודם.`;
  }
  return `${copied} מהשבוע הקודם. ${skipped.join('. ')}.`;
}

/**
 * מה נאמר בקורא מסך בזמן גרירה.
 *
 * ברירת המחדל של dnd-kit היא אנגלית, והיא מקריאה את המזהה של
 * התא: "dropped over droppable area bdeee124-f418...". זה חסר
 * תועלת גם למי ששומע וגם למי שקורא את ה DOM.
 *
 * כאן נאמר מה שקרה בעברית ובלי מזהים. השמות עצמם אינם זמינים
 * כאן, ולכן המשפט מדבר על הפעולה ולא על האנשים — מה שהשתנה
 * נקרא ממילא מהלוח אחרי ההפלה.
 */
const announcements = {
  onDragStart: () => 'התחלת גרירה. חצים להזזה, רווח להפלה, Escape לביטול.',
  onDragOver: () => undefined,
  onDragEnd: ({ over }: { over: { id: string | number } | null }) =>
    over ? 'השיבוץ הופל אל המשמרת.' : 'הגרירה הסתיימה מחוץ ללוח, ושום דבר לא השתנה.',
  onDragCancel: () => 'הגרירה בוטלה.',
};

/** אורך משמרת בדקות לפי סוג המשמרת. */
function shiftLength(board: WeekBoard | undefined, shiftTypeId: string): number {
  const type = board?.shift_types.find((one) => one.id === shiftTypeId);
  return type ? shiftMinutes(type.start_time, type.end_time) : 0;
}

/**
 * מה יקרה למכסה אם נשבץ. null = שום דבר שצריך לשאול עליו.
 *
 * נבדקות שתי המכסות בנפרד, כי מי שבסדר בשבוע יכול בהחלט לחרוג
 * בחודש. ההודעה אומרת את המספרים, ולא "חריגה" סתם: מנהל שלא
 * יודע בכמה הוא חורג לא יכול להחליט.
 */
function budgetWarning(
  board: WeekBoard,
  hours: Map<string, HoursRow> | undefined,
  employeeId: string,
  addedMinutes: number,
  monthLabel: string,
): string | null {
  if (addedMinutes <= 0) return null;

  const row = hours?.get(employeeId);
  const person = board.staff.find((one) => one.id === employeeId);
  if (!row || !person) return null;

  const weekMinutes = board.assignments
    .filter((one) => one.employee_id === employeeId)
    .reduce((total, one) => total + shiftMinutes(one.effective_start, one.effective_end), 0);

  const parts: string[] = [];

  if (row.weekly_target !== null && weekMinutes + addedMinutes > row.weekly_target * 60) {
    parts.push(`בשבוע: ${formatHours(weekMinutes + addedMinutes)} מתוך ${row.weekly_target}`);
  }
  if (row.monthly_target !== null && row.minutes + addedMinutes > row.monthly_target * 60) {
    parts.push(
      `ב${monthLabel}: ${formatHours(row.minutes + addedMinutes)} מתוך ${row.monthly_target}`,
    );
  }

  if (parts.length === 0) return null;
  return `השיבוץ הזה יוציא את ${person.name} מעבר למכסה. ${parts.join('. ')}.`;
}

const MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
] as const;

/** החודש שבו נופלת תחילת השבוע, עם הגבולות שלו ושמו. */
function monthOf(weekStartKey: string): { from: string; to: string; label: string } {
  const [year, month] = weekStartKey.split('-').map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (value: number) => String(value).padStart(2, '0');

  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(last)}`,
    label: MONTHS[month - 1],
  };
}

/**
 * סגירת יום או משמרת בסניף, מתוך הלוח.
 *
 * רק התאריך הזה. כלל קבוע ("שבת", "שישי רק בוקר") חל על כל
 * השבועות ונערך במסך ימים סגורים, כדי שלחיצה אחת בלוח של שבוע
 * אחד לא תשנה את כל השנה. זה נאמר בחלון ולא רק נכון בקוד.
 *
 * הסיבה היא שדה חובה: "סגור" בלי הסבר מייצר שאלה אצל כל מי
 * שרואה את הסידור.
 */
function CloseShiftDialog({
  board,
  cell,
  isPending,
  onCancel,
  onConfirm,
}: {
  board: WeekBoard;
  cell: { branchId: string; date: string; shiftTypeId: string };
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    branch_id: string;
    date: string;
    shift_type_id: string | null;
    label: string;
  }) => void;
}) {
  const [wholeDay, setWholeDay] = useState(false);
  const [label, setLabel] = useState('');

  const branch = board.branches.find((one) => one.id === cell.branchId);
  const shift = board.shift_types.find((one) => one.id === cell.shiftTypeId);

  const affected = board.assignments.filter(
    (one) =>
      one.branch_id === cell.branchId &&
      one.date === cell.date &&
      (wholeDay || one.shift_type_id === cell.shiftTypeId),
  ).length;

  return (
    <ConfirmDialog
      title="סגירת יום בסניף"
      tone="warn"
      isPending={isPending || label.trim() === ''}
      confirmLabel={wholeDay ? 'סגירת כל היום' : `סגירת משמרת ${shift?.name ?? ''}`}
      onCancel={onCancel}
      onConfirm={() =>
        onConfirm({
          branch_id: cell.branchId,
          date: cell.date,
          shift_type_id: wholeDay ? null : cell.shiftTypeId,
          label: label.trim(),
        })
      }
      body={
        <div className="grid gap-3">
          <p className="m-0">
            {branch?.name} · {weekdayName(cell.date)}, {formatDate(cell.date)}
          </p>

          <fieldset className="m-0 grid gap-1 border-0 p-0">
            <legend className="p-0 text-label text-muted">מה נסגר</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="scope"
                checked={!wholeDay}
                onChange={() => setWholeDay(false)}
                className="size-5"
              />
              משמרת {shift?.name} בלבד
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="scope"
                checked={wholeDay}
                onChange={() => setWholeDay(true)}
                className="size-5"
              />
              כל היום בסניף
            </label>
          </fieldset>

          <label className="grid gap-1">
            <span className="text-label text-muted">הסיבה, כפי שתוצג בסידור</span>
            <input
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="למשל: ספירת מלאי"
              className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink placeholder:text-muted"
            />
          </label>

          {affected > 0 && (
            <p className="m-0 text-body-sm text-warn">
              {affected === 1
                ? 'שיבוץ אחד שנמצא כאן יוסר.'
                : `${affected} שיבוצים שנמצאים כאן יוסרו.`}
            </p>
          )}

          <p className="m-0 text-label text-muted">
            הסגירה חלה על התאריך הזה בלבד. יום קבוע כמו שבת נקבע במסך ימים סגורים.
          </p>
        </div>
      }
    />
  );
}

const STATUS_LABEL: Record<string, { text: string; tone: 'good' | 'warn' | 'bad' }> = {
  collecting: { text: 'אוסף אילוצים', tone: 'warn' },
  drafting: { text: 'בעבודה', tone: 'warn' },
  published: { text: 'פורסם', tone: 'good' },
  archived: { text: 'בארכיון', tone: 'bad' },
};

/**
 * מצב הסניף, ודרך הכניסה לפעולות שהן שלו ולא של הלוח: פרסום,
 * מועד פרסום מתוזמן, פתיחה מחדש.
 *
 * בכותרת הסניף ולא ברשימה נפרדת של כל הסניפים: זו עובדה על סניף
 * אחד, והמקום שבו היא נחוצה הוא מעל הלוח שלו.
 */
function BranchStatus({
  branch,
  scheduled,
}: {
  branch: WeekBoard['branches'][number];
  /** מועד פרסום מתוזמן, לפי מזהה תקופה. ריק בזמן טעינה. */
  scheduled?: Map<string, string>;
}) {
  const status = branch.status ? STATUS_LABEL[branch.status] : null;
  const at = branch.period_id ? scheduled?.get(branch.period_id) : undefined;

  return (
    <span className="flex flex-wrap items-center gap-2">
      {status ? (
        <StatusBadge tone={status.tone}>{status.text}</StatusBadge>
      ) : (
        <span className="text-label text-muted">אין שבוע</span>
      )}

      {/*
        תזמון פתוח נאמר כאן. מנהל שעורך סידור שעומד לצאת בעוד
        שעתיים צריך לדעת את זה בלי להיכנס לסניף.
      */}
      {at && branch.status !== 'published' && (
        <StatusBadge tone="warn">פרסום ב {formatDateTime(at)}</StatusBadge>
      )}

      {branch.period_id && (
        <Link to={`/admin/schedule/${branch.period_id}`} className="text-label text-ink underline">
          {branch.status === 'published' ? 'פתיחה מחדש' : at ? 'מועד הפרסום' : 'פרסום'}
        </Link>
      )}
    </span>
  );
}

/**
 * כמה מהשבוע כבר מאויש, כמספר וכפס.
 *
 * הפס עונה בלי לקרוא, והמספר הוא התשובה המדויקת. מאה אחוז מקבל
 * מילה ולא רק צבע (כלל 10), כי "ירוק" לבדו אינו אומר "סיימת".
 */
function BranchFill({ filled, required }: { filled: number; required: number }) {
  if (required === 0) return null;

  const percent = Math.round((filled / required) * 100);
  const done = filled >= required;

  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="block h-1.5 w-24 overflow-hidden rounded-pill bg-olive-soft"
      >
        <span
          className={cn('block h-full rounded-pill', done ? 'bg-good' : 'bg-olive')}
          style={{ width: `${percent}%` }}
        />
      </span>
      <span className={cn('text-label', done ? 'font-medium text-good' : 'text-muted')}>
        {done ? 'מאויש במלואו' : `מאויש ${filled} מתוך ${required}`}
      </span>
    </span>
  );
}

/** בחירת הסניפים שמוצגים. ריק = כולם. */
function BranchPicker({
  branches,
  selected,
  onChange,
}: {
  branches: WeekBoard['branches'];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  if (branches.length <= 1) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onChange([])}
        aria-pressed={selected.length === 0}
        className={cn(
          'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm font-medium transition-colors',
          selected.length === 0
            ? 'border-charcoal bg-charcoal text-on-charcoal'
            : 'border-muted bg-transparent text-ink hover:bg-cream',
        )}
      >
        כל הסניפים
      </button>

      {branches.map((branch) => {
        const isOn = selected.includes(branch.id);
        return (
          <button
            key={branch.id}
            type="button"
            aria-pressed={isOn}
            onClick={() =>
              onChange(isOn ? selected.filter((id) => id !== branch.id) : [...selected, branch.id])
            }
            className={cn(
              'inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-pill border px-3 text-body-sm transition-colors',
              isOn
                ? 'border-ink font-semibold text-ink'
                : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
            )}
            // סניף שנבחר נצבע בצבע שלו, ולא רק במסגרת כהה יותר.
            // זה גם מה שמחבר בין הצ'יפ לבין הפס שעל התא בלוח.
            style={isOn && branch.color ? { backgroundColor: branch.color } : undefined}
          >
            {isOn ? (
              <Check size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />
            ) : (
              <span
                aria-hidden="true"
                className="size-3 shrink-0 rounded-pill border border-line"
                style={branch.color ? { backgroundColor: branch.color } : undefined}
              />
            )}
            {branch.name}
          </button>
        );
      })}
    </div>
  );
}
